import React from 'react';
import { Box } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import { DistanceField, NumericField } from '@paragon/material';

import { PsfPlfToggle } from '../elements/PsfPlfToggle';
import { magnitudeUnitToEndAdornmentText } from '../manual-loads/LoadMagnitudeSettings';

export type ParapetLoadDirection = 'Left' | 'Right';

export type ParapetLoadFields = {
  readonly startMagnitude: number;
  readonly endMagnitude: number;
  readonly magnitudeUnit: 'PoundsPerSquareFoot' | 'PoundsPerLinearFoot';
  readonly direction: ParapetLoadDirection;
  readonly startY: number;
  readonly endY: number;
};

type Option<T> = {
  name: string;
  value: T;
};

const getName = <T extends string>(option: Option<T>) => option.name;
const getValue = <T extends string>(option: Option<T>) => option.value;

type LoadDirectionOptions = { [key in ParapetLoadDirection]: Option<ParapetLoadDirection> };
const loadDirectionOptions: LoadDirectionOptions = {
  Left: {
    name: 'Left',
    value: 'Left',
  },
  Right: {
    name: 'Right',
    value: 'Right',
  },
};

const multiplier = 100;
function round(value: number) {
  return Math.round(value * multiplier) / multiplier;
}

type Props = {
  readonly startMagnitudeDisabled?: boolean;
  readonly endMagnitudeDisabled?: boolean;

  readonly loadMemberTypeIsSelected: boolean;
  readonly parapetLoadFields: ParapetLoadFields;
  readonly trussSpacing: number;

  readonly onChange: (key: keyof ParapetLoadFields) => (value: any) => void;
};

export default class ParapetLoadInsertionFields extends React.Component<Props> {
  loadMagnitudeChange = (value: number) => {
    this.props.onChange('startMagnitude')(value);
    this.props.onChange('endMagnitude')(value);
  };

  onChangeOption =
    <T extends string>(key: keyof ParapetLoadFields) =>
    (option: Option<T>) => {
      this.props.onChange(key)(option.value);
    };

  render() {
    const { startMagnitudeDisabled, endMagnitudeDisabled, parapetLoadFields, loadMemberTypeIsSelected, trussSpacing } =
      this.props;
    const { direction, startY, endY, startMagnitude, endMagnitude, magnitudeUnit } = parapetLoadFields;

    const disableStartMagnitude = startMagnitudeDisabled || !loadMemberTypeIsSelected;
    const disableEndMagnitude = endMagnitudeDisabled || !loadMemberTypeIsSelected;

    return (
      <>
        <Box sx={{ display: 'flex' }}>
          <PropertySelectField
            sx={{ m: 1 }}
            property="Direction"
            value={loadDirectionOptions[direction]}
            options={Object.values(loadDirectionOptions)}
            getName={getName}
            getValue={getValue}
            submit={this.onChangeOption('direction')}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <DistanceField
            sx={{ m: 1 }}
            label="Start Point Y"
            value={round(startY)}
            onSubmit={this.props.onChange('startY')}
          />
          <DistanceField sx={{ m: 1 }} label="End Point Y" value={round(endY)} onSubmit={this.props.onChange('endY')} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {!disableStartMagnitude && (
            <PsfPlfToggle sx={{ m: 1 }} magnitudeUnit={magnitudeUnit} onChange={this.props.onChange('magnitudeUnit')} />
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {!disableEndMagnitude && (
            <PsfPlfToggle sx={{ m: 1 }} magnitudeUnit={magnitudeUnit} onChange={this.props.onChange('magnitudeUnit')} />
          )}
        </Box>
        <Box sx={{ display: 'flex' }}>
          <NumericField
            sx={{ m: 1 }}
            label="Load Start Magnitude"
            disabled={disableStartMagnitude}
            value={startMagnitude}
            onSubmit={this.props.onChange('startMagnitude')}
            endAdornment={magnitudeUnitToEndAdornmentText(magnitudeUnit)}
          />
        </Box>
        <Box sx={{ display: 'flex' }}>
          <NumericField
            sx={{ m: 1 }}
            label="Load End Magnitude"
            disabled={disableEndMagnitude}
            value={endMagnitude}
            onSubmit={this.props.onChange('endMagnitude')}
            endAdornment={magnitudeUnitToEndAdornmentText(magnitudeUnit)}
          />
        </Box>
        <Box sx={{ display: 'flex' }}>
          <DistanceField disabled sx={{ m: 1 }} label="Truss Spacing" value={trussSpacing} fullWidth={false} />
        </Box>
      </>
    );
  }
}
